import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { HTTPError } from 'src/api/createApiClient.types';
import { cloudConnectSubscriptionApi } from 'src/api/endpoints';
import type {
  CloudConnectAddress,
  CloudConnectPayment,
  CloudConnectPaymentMethodApply,
  CloudConnectSubscription,
  ConnectInterval,
} from 'src/api/endpoints/cloudConnect.types';

import { queryKeys } from './queryKeys';

export const getCloudConnectSubscription = () =>
  cloudConnectSubscriptionApi.getSubscription();

export const getCloudConnectPaymentHistory = (): Promise<
  CloudConnectPayment[]
> => cloudConnectSubscriptionApi.getPaymentHistory();

export const createQueryCloudConnectSubscription = () =>
  queryOptions({
    queryKey: queryKeys.cloudConnectSubscriptions.all,
    queryFn: getCloudConnectSubscription,
    staleTime: 10_000,
  });

export const createQueryCloudConnectPaymentHistory = () =>
  queryOptions({
    queryKey: queryKeys.cloudConnectSubscriptions.paymentHistory,
    queryFn: getCloudConnectPaymentHistory,
    staleTime: Infinity,
  });

// Used while awaiting an out-of-band payment approval (e.g. 3D Secure in a separate Stripe tab): the backend's
// webhook that flips the subscription over can lag behind the customer returning to this tab, so we poll for a
// while as a fallback to catch it. Relies on TanStack Query's built-in focus-awareness - `refetchInterval` already
// pauses while this tab isn't focused/visible (default `refetchIntervalInBackground: false`) and `refetchOnWindowFocus`
// (also on by default) refetches immediately when the customer returns - so we don't need to hand-roll either of
// those behaviors here.
const PENDING_PAYMENT_POLLING_INTERVAL_MS = 3000;
const PENDING_PAYMENT_POLLING_MAX_ATTEMPTS = 20;

export const useCloudConnectSubscription = ({
  enabled = true,
  pollForUpdates = false,
}: { enabled?: boolean; pollForUpdates?: boolean } = {}) =>
  useQuery({
    ...createQueryCloudConnectSubscription(),
    enabled,
    refetchInterval: pollForUpdates
      ? (query) =>
          query.state.dataUpdateCount < PENDING_PAYMENT_POLLING_MAX_ATTEMPTS
            ? PENDING_PAYMENT_POLLING_INTERVAL_MS
            : false
      : false,
  });

export const useCloudConnectPaymentHistory = () =>
  useQuery(createQueryCloudConnectPaymentHistory());

export const useCancelCloudConnectSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.cloudConnectSubscriptions.cancel,
    mutationFn: cloudConnectSubscriptionApi.cancelSubscription,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.cloudConnectSubscriptions.all,
      }),
  });
};

export const useReactivateCloudConnectSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.cloudConnectSubscriptions.reactivate,
    mutationFn: cloudConnectSubscriptionApi.reactivateSubscription,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.cloudConnectSubscriptions.all,
      }),
  });
};

export const useUpdateCloudConnectBillingInterval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.cloudConnectSubscriptions.billingIntervalUpdate,
    mutationFn: (billingInterval: ConnectInterval) =>
      cloudConnectSubscriptionApi.changeSubscriptionBillingInterval(
        billingInterval
      ),
    onSuccess: (result) => {
      // Only invalidate once the change has actually taken effect. If it requires a payment action, the
      // subscription hasn't changed yet, so refetching now would be pointless.
      if (result.status !== 'success') return;

      return queryClient.invalidateQueries({
        queryKey: queryKeys.cloudConnectSubscriptions.all,
      });
    },
  });
};

export const useUpdateCloudConnectAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (address: CloudConnectAddress) =>
      cloudConnectSubscriptionApi.updateAddress(address),
    onSuccess: (subscription) => {
      if (!subscription) {
        return queryClient.invalidateQueries({
          queryKey: queryKeys.cloudConnectSubscriptions.all,
        });
      }

      // The response is the updated subscription. Update the cache directly
      // instead of refetching.
      queryClient.setQueryData(
        queryKeys.cloudConnectSubscriptions.all,
        subscription
      );
    },
  });
};

export const useSetupCloudConnectPaymentMethod = () =>
  useMutation({
    mutationKey: queryKeys.cloudConnectSubscriptions.paymentMethodSetup,
    mutationFn: () => cloudConnectSubscriptionApi.setupPaymentMethod(),
  });

export const useApplyCloudConnectPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.cloudConnectSubscriptions.paymentMethodApply,
    mutationFn: (body: CloudConnectPaymentMethodApply) =>
      cloudConnectSubscriptionApi.applyPaymentMethod(body),
    onSuccess: (subscription) => {
      if (!subscription) {
        return queryClient.invalidateQueries({
          queryKey: queryKeys.cloudConnectSubscriptions.all,
        });
      }

      queryClient.setQueryData(
        queryKeys.cloudConnectSubscriptions.all,
        subscription
      );
    },
  });
};

export const shouldFallbackToStoreApi = (
  subscription: CloudConnectSubscription | null,
  error?: unknown
): boolean =>
  subscription?.productId === 'connect_multiple' ||
  (error instanceof HTTPError && error.response.status >= 500);
